import { useEffect, useRef, useState, type ReactNode } from "react";
import { audio } from "../../lib/audio";
import { Btn } from "./ui";
import { Browser, Caption, BootScene, Desktop } from "./idorStory";
import { DarkMarket } from "./attackVisuals";
import type { MfitGuideText } from "../../scenario/types";
import { tx, txr } from "../../lib/tx";

/**
 * SQL injection, done live and in order. The learner sits at a real-looking
 * bookshop staff portal (address pre-filled, one-click sign-in), then runs the
 * lookup field one guided step at a time: a real order, a single quote (DB
 * error), a SLEEP payload (the query time jumps from 0.04s to 4.01s, shown with
 * every result and in a DevTools Network panel), then a UNION payload that
 * prints the users table onto the page. The steps are gated so the learner
 * cannot jump ahead to the UNION. The automated dump -> credential stuffing ->
 * market escalation and the teaching tail follow. Nothing real runs.
 */
interface StoryProps { onFinish: () => void; onSkip: () => void; onGuide?: (g: MfitGuideText | null) => void }

const SITE = "portal.novella.example";
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const ORDER = { ref: "NB-1001", who: "Mia Keller", item: "Dune (paperback)", total: "CHF 18.00" };
const USERS = [
  { email: "jborg@novella.example", pw: "novella24" },
  { email: "mia.keller@proton.example", pw: "Summer2023!" },
  { email: "t.brandt@gmx.example", pw: "Liverpool99" },
  { email: "lena.vogt@bluewin.example", pw: "hunter2" },
  { email: "a.weber@hotmail.example", pw: "P@ssw0rd" },
];
const PLATFORMS = ["Work email", "Work SSO", "Social", "Cloud drive", "Bank"];
const MATCH = [
  [true, true, false, false, true],
  [false, false, true, true, false],
  [true, false, false, false, false],
  [false, true, true, false, false],
  [true, false, true, false, true],
];

type StepKind = "order" | "quote" | "sleep" | "union";
interface Step { payload: string; kind: StepKind; time: string; guide: MfitGuideText; explain: ReactNode }
const STEPS: Step[] = [
  {
    payload: "NB-1001",
    kind: "order",
    time: "0.04",
    guide: { title: "Look up a real order", body: "The reference NB-1001 is already in the box. Click Search.", actionLabel: "Search" },
    explain: <>{txr("A normal lookup returns one row, instantly. Note the <0>query time: 0.04 s</0>. Remember it.", {}, [<b />])}</>,
  },
  {
    payload: "'",
    kind: "quote",
    time: "0.05",
    guide: { title: "Test the field", body: "A single quote ' is in the box now. Click Search.", actionLabel: "Search" },
    explain: <>{txr("One quote broke the query and the database threw an error. That error proves your text is run as <0>part of the SQL command</0>, not treated as a value. The field is injectable.", {}, [<b />])}</>,
  },
  {
    payload: "NB-1001' AND SLEEP(4)-- ",
    kind: "sleep",
    time: "4.01",
    guide: { title: "Time the query", body: "The SLEEP payload is ready. Click Search and watch the query time.", actionLabel: "Search" },
    explain: <>{txr("No rows came back — but the database <0>waited 4 seconds</0>, so it actually ran your <1>SLEEP(4)</1> command. The real lookup took 0.04 s; this took <2>4.01 s</2>. That 100× jump confirms the injection even when nothing is shown on screen. This is <3>blind SQL injection</3>: ask yes/no questions and read the answer from the response time.", {}, [<b />, <code />, <b />, <b />])}</>,
  },
  {
    payload: "' UNION SELECT email,password FROM users-- ",
    kind: "union",
    time: "0.06",
    guide: { title: "Pull the data out", body: "The UNION payload is ready. Click Search to print the users table.", actionLabel: "Search" },
    explain: <>{txr("UNION glued a second query onto the end, so the order lookup now returns rows from the <0>users</0> table instead — every staff and customer login, printed straight onto the page.", {}, [<b />])}</>,
  },
];

/* ---------------------------------------------------------- shared mini-helpers */
function Rail({ onSkip }: { onSkip: () => void }) {
  return (
    <div className="dd-os-rail">
      <span className="dd-os-rail-badge"><span className="dd-attdot" aria-hidden="true" />{txr("Live attack · you are the attacker", {})}</span>
      <button type="button" className="dd-link" onClick={onSkip}>{tx("Skip the demo")}</button>
    </div>
  );
}
function Shell({ onSkip, children }: { onSkip: () => void; children: ReactNode }) {
  return (
    <div className="dd-os">
      <div className="dd-os-screen">{children}</div>
      <Rail onSkip={onSkip} />
    </div>
  );
}
function Term({ title, lines }: { title: string; lines: string[] }) {
  useEffect(() => {
    const timers = lines.map((l, i) => setTimeout(() => {
      if (l.startsWith("!")) audio.buzz();
      else if (l.startsWith(">")) audio.chimeGood();
      else audio.ui("key", 0.28);
    }, 220 + i * 300));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="dd-term2">
      <div className="dd-term2-h"><span className="dd-term-dot" aria-hidden="true" />{title}</div>
      <div className="dd-term2-b">
        {lines.map((l, i) => {
          const bad = l.startsWith("!"); const good = l.startsWith(">");
          return <div key={i} className={`dd-term2-l${bad ? " is-bad" : ""}${good ? " is-good" : ""}`} style={{ animationDelay: `${i * 0.3}s` }}>{bad || good ? l.slice(1) : l}</div>;
        })}
      </div>
    </div>
  );
}
function Market({ title, lines, price, ctaLabel, onDone, children }: { title: string; lines: string[]; price: string; ctaLabel: string; onDone: () => void; children: ReactNode }) {
  useEffect(() => { audio.notification(); }, []);
  return (
    <div className="dd-dump">
      <DarkMarket vendor="fresh_dumps" rating={tx("98.7% (2,413 sales)")} title={title} bullets={lines} price={price} posted={tx("posted by you, just now")} />
      <Caption cta ctaLabel={ctaLabel} onCta={onDone}>{children}</Caption>
    </div>
  );
}
function Here() {
  return <span className="dd-here" aria-hidden="true">{tx("click")}</span>;
}

/* ---------------------------------------------------------- the live portal */
interface NetRow { q: string; status: number; ms: number }

function SqlPortal({ onDone, onGuide }: { onDone: () => void; onGuide?: (g: MfitGuideText | null) => void }) {
  const [phase, setPhase] = useState<"addr" | "login" | "dash">("addr");
  const [addr, setAddr] = useState(SITE);
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [q, setQ] = useState(STEPS[0].payload);
  const [ran, setRan] = useState(-1); // highest step index already run
  const [running, setRunning] = useState(false);
  const [usersN, setUsersN] = useState(0);
  const [dev, setDev] = useState(false);
  const [net, setNet] = useState<NetRow[]>([]);
  const busy = useRef(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const current = Math.min(ran + 1, STEPS.length - 1);
  const finished = ran >= STEPS.length - 1;

  // pre-fill the next step's payload once a step has run
  useEffect(() => { if (ran >= 0 && ran < STEPS.length - 1) setQ(STEPS[ran + 1].payload); }, [ran]);

  // Bring the fresh result into view (and keep the Network panel below it in
  // frame) so the learner never has to scroll the portal by hand.
  useEffect(() => {
    if (ran < 0 || running) return;
    const t = window.setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 90);
    return () => window.clearTimeout(t);
  }, [ran, running, dev]);

  // per-step guidance into the top-right card
  useEffect(() => {
    if (!onGuide) return;
    if (phase === "addr") onGuide({ title: "Open the staff portal", body: "The address is already typed in. Click Go to open the portal.", actionLabel: "Go" });
    else if (phase === "login") onGuide(u.trim() && p.trim()
      ? { title: "Sign in", body: "The staff login is filled in. Now click Sign in.", actionLabel: "Sign in" }
      : { title: "Sign in", body: "Click Insert staff login to fill the form with the sticky-note credentials.", actionLabel: "Insert staff login" });
    else if (finished) onGuide({ title: "You printed the users table", body: "Click Automate the full dump to continue.", actionLabel: "Automate the full dump" });
    else onGuide(STEPS[current].guide);
  }, [phase, current, finished, u, p, onGuide]);

  const key = () => audio.ui("key", 0.3);

  const run = async () => {
    if (busy.current || phase !== "dash" || finished) return;
    const step = STEPS[current];
    busy.current = true; setRunning(true); setUsersN(0);
    const slow = step.kind === "sleep";
    if (slow) audio.buzz();
    await sleep(slow ? 3400 : 470);
    setRunning(false); busy.current = false;
    const ms = slow ? 4010 : step.kind === "quote" ? 50 : 42;
    setNet((n) => [{ q: step.payload.trim(), status: step.kind === "quote" ? 500 : 200, ms }, ...n].slice(0, 6));
    if (step.kind === "order") audio.chimeGood();
    else if (step.kind === "quote") audio.errorBuzz();
    else if (step.kind === "sleep") { audio.ui("alert"); setDev(true); }
    // Advance the result/guide to the step that just ran BEFORE the UNION rows
    // animate, so the card never lags a step behind the input and network panel.
    setRan(current);
    if (step.kind === "union") {
      for (let i = 1; i <= USERS.length; i++) { setUsersN(i); audio.dataBlip(); await sleep(230); }
    }
  };

  /* ---- address bar (pre-filled) ---- */
  if (phase === "addr") {
    return (
      <Browser url={addr || " "} tone="attacker" label={tx("Your browser")}>
        <div className="dd-sqlx dd-sqlx-newtab">
          <div className="dd-sqlx-omni">
            <input className="dd-sqlx-addr" value={addr} onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { audio.ui("pageOpen"); setPhase("login"); } }} aria-label={tx("address bar")} />
            <Btn primary pulse onClick={() => { audio.ui("pageOpen"); setPhase("login"); }}>{tx("Go")}</Btn>
            <Here />
          </div>
        </div>
      </Browser>
    );
  }

  /* ---- login (insert button, gated sign-in) ---- */
  if (phase === "login") {
    const ok = !!(u.trim() && p.trim());
    return (
      <Browser url={`${SITE}/signin`} secure tone="attacker" label={tx("Your browser")}>
        <div className="dd-sqlx dd-sqlx-login">
          <div className="dd-sqlx-card">
            <b className="dd-sqlx-brand">{txr("Novella <0>Staff Portal</0>", {}, [<span />])}</b>
            <label>{tx("Username")}</label>
            <input className="dd-sqlx-input" value={u} placeholder={tx("username")} onChange={(e) => setU(e.target.value)} onKeyDown={key} />
            <label>{tx("Password")}</label>
            <input className="dd-sqlx-input" type="password" value={p} placeholder={tx("password")} onChange={(e) => setP(e.target.value)} onKeyDown={key} />
            {!ok && (
              <button type="button" className="dd-sqlx-insert cf-pulse-target" onClick={() => { setU("jborg"); setP("novella24"); audio.ui("key"); }}>
                {txr("Insert staff login (jborg / novella24)<0>Click here</0>", {}, [<span className="dd-os-clickhere" />])}
              </button>
            )}
            <div className="dd-sqlx-go">
              <Btn primary pulse={ok} disabled={!ok} onClick={() => { audio.logon(); setPhase("dash"); }}>{tx("Sign in")}</Btn>
              {ok ? <Here /> : null}
            </div>
          </div>
        </div>
      </Browser>
    );
  }

  /* ---- dashboard: ordered live exploit ---- */
  const shown = ran >= 0 ? STEPS[ran] : null;
  return (
    <Browser url={`${SITE}/orders`} secure tone="attacker" label={tx("Signed in as jborg")}>
      <div className={`dd-sqlx dd-sqlx-dash${dev ? " has-dev" : ""}`}>
        <div className="dd-sqlx-main">
          <div className="dd-sqlx-bar">
            <b className="dd-sqlx-brand">{txr("Novella <0>Staff Portal</0>", {}, [<span />])}</b>
            <button type="button" className={`dd-sqlx-f12${dev ? " is-on" : ""}`} onClick={() => { setDev((d) => !d); audio.click(); }}>{txr("F12 · Network", {})}</button>
          </div>
          <div className="dd-sqlx-form">
            <label>{tx("Look up an order by reference")} {!finished && <span className="dd-sqlx-stepn">{txr("step {v} of {length}", { v: current + 1, length: STEPS.length })}</span>}</label>
            <div className="dd-sqlx-row">
              <input className="dd-sqlx-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { key(); if (e.key === "Enter") void run(); }} aria-label={tx("order reference")} disabled={finished} />
              {!finished && <Btn primary pulse={!running} disabled={running} onClick={() => void run()}>{tx("Search")}</Btn>}
              {!finished && !running ? <Here /> : null}
            </div>
          </div>

          {running && <div className="dd-sqlx-wait"><span className="dd-os-spin" aria-hidden="true" /> {tx("running query")}{"\u2026"}</div>}

          {!running && shown && (
            <div className="dd-sqlx-result" ref={resultRef}>
              <div className="dd-sqlx-rhead">
                <span>
                  {shown.kind === "order" && tx("1 row returned")}
                  {shown.kind === "quote" && tx("Request failed — database error")}
                  {shown.kind === "sleep" && tx("0 rows returned")}
                  {shown.kind === "union" && tx("{usersN} row{v} returned", { usersN, v: usersN === 1 ? "" : "s" })}
                </span>
                <span className={`dd-sqlx-time${shown.kind === "sleep" ? " is-slow" : ""}`}>{txr("query time {time} s{v}", { time: shown.time, v: shown.kind === "sleep" ? tx("  ⚠ 100× slower") : "" })}</span>
              </div>

              {shown.kind === "order" && (
                <table className="dd-sqlx-tab"><thead><tr><th>{tx("Reference")}</th><th>{tx("Customer")}</th><th>{tx("Item")}</th><th>{tx("Total")}</th></tr></thead>
                  <tbody><tr><td>{ORDER.ref}</td><td>{ORDER.who}</td><td>{ORDER.item}</td><td>{ORDER.total}</td></tr></tbody></table>
              )}
              {shown.kind === "quote" && (
                <div className="dd-sqlx-err">{txr("<0>Database error</0>{br}SQLSTATE[42000]: Syntax error or access violation near ''' at line 1{br2}<1>SELECT * FROM orders WHERE ref = '''</1>", { br: <br />, br2: <br /> }, [<b />, <code />])}</div>
              )}
              {shown.kind === "sleep" && <div className="dd-sqlx-empty">{tx("No order found.")}</div>}
              {shown.kind === "union" && (
                <table className="dd-sqlx-tab is-leak"><thead><tr><th>{tx("Reference")}</th><th>{tx("Customer")}</th><th>{tx("Item")}</th><th>{tx("Total")}</th></tr></thead>
                  <tbody>
                    {USERS.slice(0, usersN).map((us) => <tr key={us.email}><td className="dd-sqlx-leak">{us.email}</td><td className="dd-sqlx-leak">{us.pw}</td><td /><td /></tr>)}
                    {usersN >= USERS.length && <tr><td className="dd-sqlx-more">{"\u2026"} {tx("8,412 rows from table users")}</td><td /><td /><td /></tr>}
                  </tbody></table>
              )}

              <div className="dd-sqlx-explain">{shown.explain}</div>
            </div>
          )}

          {finished && (
            <div className="dd-sqlx-final">
              <Btn primary pulse onClick={onDone}>{tx("Automate the full dump")} {"\u2192"}</Btn>
              <Here />
            </div>
          )}
        </div>

        {dev && (
          <div className="dd-sqlx-dev">
            <div className="dd-sqlx-dev-h">{txr("Network · /orders/lookup — compare the Time column", {})}</div>
            <div className="dd-sqlx-net">
              <div className="dd-sqlx-netrow is-head"><span>{tx("input")}</span><span>{tx("status")}</span><span>{tx("time")}</span></div>
              {net.length === 0 && <div className="dd-sqlx-netrow is-empty"><span>{tx("run a search to see requests")}</span><span /><span /></div>}
              {net.map((r, i) => (
                <div key={i} className={`dd-sqlx-netrow${r.status >= 500 ? " is-err" : ""}${r.ms > 1000 ? " is-slow" : ""}`}>
                  <span className="dd-sqlx-netq">{r.q.length > 26 ? r.q.slice(0, 26) + "\u2026" : r.q}</span>
                  <span>{r.status}</span>
                  <span className="dd-sqlx-netms">{txr("{ms} ms", { ms: r.ms })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Browser>
  );
}

/* ---------------------------------------------------------- escalation */
function StuffMatrix({ onDone }: { onDone: () => void }) {
  // Sound each account takeover as its check mark lands (CSS stagger 0.1s/cell).
  useEffect(() => {
    const timers: number[] = [];
    USERS.forEach((_, ri) => PLATFORMS.forEach((_, ci) => {
      if (MATCH[ri][ci]) timers.push(window.setTimeout(() => audio.dataBlip(), (ri * PLATFORMS.length + ci) * 100));
    }));
    return () => timers.forEach(clearTimeout);
  }, []);
  return (
    <div className="dd-stuff">
      <div className="dd-stuff-h">{txr("Credential stuffing — testing each stolen password on other sites", {})}</div>
      <table className="dd-stuff-tab">
        <thead><tr><th aria-label={tx("user")} />{PLATFORMS.map((pl) => <th key={pl}>{pl}</th>)}</tr></thead>
        <tbody>
          {USERS.map((user, ri) => (
            <tr key={user.email}>
              <td className="dd-stuff-u">{user.email}</td>
              {PLATFORMS.map((pl, ci) => {
                const hit = MATCH[ri][ci];
                return <td key={pl} className={`dd-stuff-c${hit ? " is-hit" : ""}`} style={{ animationDelay: `${(ri * PLATFORMS.length + ci) * 0.1}s` }}>{hit ? "\u2713" : "\u00b7"}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <Caption cta ctaLabel={tx("Sell the dump")} onCta={onDone}>
        {txr("People reuse passwords, so the shop password often unlocks their email, work login and bank — each {b} is a full takeover on another site.", { b: <b>{"\u2713"}</b> })}
      </Caption>
    </div>
  );
}

/* ---------------------------------------------------------- driver */
export function SqliStory({ onFinish, onSkip, onGuide }: StoryProps) {
  const [step, setStep] = useState<"boot" | "desktop" | "portal" | "dump" | "stuff" | "market">("boot");
  useEffect(() => {
    if (!onGuide) return;
    // SqlPortal owns the guide while step === "portal"; cover every other step here.
    if (step === "boot") onGuide({ title: "You are the attacker", body: "Click Power on to switch on the machine and begin.", actionLabel: "Power on" });
    else if (step === "desktop") onGuide({ title: "Open the browser", body: "Click the Web browser icon.", actionLabel: "Web browser" });
    else if (step === "dump") onGuide({ title: "Automate the whole database", body: "Your by-hand UNION proved it; a tool now mirrors every table. Click Try the passwords elsewhere.", actionLabel: "Try the passwords elsewhere" });
    else if (step === "stuff") onGuide({ title: "Reuse the stolen passwords", body: "Each stolen password is tried on other sites. Click Sell the dump.", actionLabel: "Sell the dump" });
    else if (step === "market") onGuide({ title: "Sell the cards", body: "The dump is listed on a darknet market. Click See why it worked, and the fix.", actionLabel: "See why it worked, and the fix" });
  }, [step, onGuide]);
  return (
    <Shell onSkip={onSkip}>
      {step === "boot" && <BootScene onDone={() => setStep("desktop")} />}
      {step === "desktop" && <Desktop onBrowser={() => setStep("portal")} />}
      {step === "portal" && <SqlPortal onDone={() => { audio.downloadChime(); setStep("dump"); }} onGuide={onGuide} />}

      {step === "dump" && (
        <div className="dd-inj-run">
          <Term
            title={tx("sqlmap · automate the rest")}
            lines={[
              "$ sqlmap -u https://portal.novella.example/orders/lookup --dump",
              "!parameter 'ref' is injectable (time-based blind + UNION)",
              "[*] fetching tables: orders, users, payment_tokens",
              "[*] dumping table 'users' (8,412 rows)\u2026",
              ">done. saved to users.csv",
            ]}
          />
          <Caption cta ctaLabel={tx("Try the passwords elsewhere")} onCta={() => setStep("stuff")}>
            {tx("Your by-hand UNION proved it; a tool now mirrors the whole database automatically.")}
          </Caption>
        </div>
      )}

      {step === "stuff" && <StuffMatrix onDone={() => setStep("market")} />}

      {step === "market" && (
        <Market
          title={tx("Novella customers — 8,412 logins")}
          lines={["Emails + passwords, dumped via the order lookup", "1,204 reused on Microsoft 365, banking or social", "Buyers get working cross-site accounts"]}
          price={tx("2.2 BTC")}
          ctaLabel={tx("See why it worked, and the fix")}
          onDone={onFinish}
        >
          {txr("One lookup box that pasted your text into its SQL became every customer’s password.", {})}
        </Market>
      )}
    </Shell>
  );
}
